// Historique des rapports briques & pavés : par semaine ou par mois, synthèse de la production par article.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, AssignmentOutlined, CategoryOutlined, ChevronLeft, ChevronRight, CommentOutlined, DeleteSweepOutlined, PrintOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { briquesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { apiError, dateFr } from '../../utils/format';
import { tokens } from '../../theme';

const p2 = (n) => String(n).padStart(2, '0');
const isoD = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const nb = (n) => Number(n || 0).toLocaleString('fr-FR');
function periode(mode, ancre) {
  const d = new Date(`${ancre}T12:00:00`);
  if (mode === 'semaine') {
    const lundi = new Date(d); lundi.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const dim = new Date(lundi); dim.setDate(lundi.getDate() + 6);
    return { from: isoD(lundi), to: isoD(dim), label: `Semaine du ${dateFr(isoD(lundi))} au ${dateFr(isoD(dim))}` };
  }
  if (mode === 'mois') {
    const debut = new Date(d.getFullYear(), d.getMonth(), 1); const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from: isoD(debut), to: isoD(fin), label: debut.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) };
  }
  return { from: undefined, to: undefined, label: 'Tous les rapports' };
}
function decaler(mode, ancre, sens) {
  const d = new Date(`${ancre}T12:00:00`);
  if (mode === 'semaine') d.setDate(d.getDate() + 7 * sens); else d.setMonth(d.getMonth() + sens, 1);
  return isoD(d);
}

const RapportsBriques = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const [mode, setMode] = useState('mois');
  const [ancre, setAncre] = useState(isoD(new Date()));
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const per = useMemo(() => periode(mode, ancre), [mode, ancre]);

  useEffect(() => {
    setD(null);
    briquesAPI.rapports({ from: per.from, to: per.to }).then((r) => setD(r.data)).catch((e) => setError(apiError(e)));
  }, [per.from, per.to]);

  const total = d?.synthese.reduce((s, x) => s + x.quantite, 0) || 0;
  const rebut = d?.synthese.reduce((s, x) => s + x.rebut, 0) || 0;
  const peutSaisir = hasRole('briques') || hasRole('resp_briques');

  return (
    <Layout title="Rapports briques & pavés" plain
      actions={(
        <>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>
          {peutSaisir && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => navigate('/briques/rapport')}>Nouveau rapport</Button>}
        </>
      )}>
      {location.state?.info && <Alert severity="success" sx={{ mb: 2 }}>{location.state.info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)} aria-label="Période">
          <ToggleButton value="semaine">Semaine</ToggleButton><ToggleButton value="mois">Mois</ToggleButton><ToggleButton value="tout">Tout</ToggleButton>
        </ToggleButtonGroup>
        {mode !== 'tout' && (
          <>
            <IconButton size="small" aria-label="Période précédente" onClick={() => setAncre(decaler(mode, ancre, -1))}><ChevronLeft /></IconButton>
            <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 210, textAlign: 'center' }}>{per.label}</Typography>
            <IconButton size="small" aria-label="Période suivante" onClick={() => setAncre(decaler(mode, ancre, 1))}><ChevronRight /></IconButton>
            <Button size="small" onClick={() => setAncre(isoD(new Date()))}>Aujourd'hui</Button>
          </>
        )}
      </Box>
      <Typography sx={{ display: 'none', '@media print': { display: 'block' }, fontWeight: 700, mb: 1, textTransform: 'capitalize' }}>DOCC — Briques & pavés — {per.label}</Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi compact icon={AssignmentOutlined} tint="navy" label="Rapports" value={d ? d.rapports.length : '…'} sub="sur la période" /></Grid>
        <Grid item xs={6} md={3}><Kpi compact icon={CategoryOutlined} tint="green" label="Produit" value={nb(total)} sub="toutes unités confondues" /></Grid>
        <Grid item xs={6} md={3}><Kpi compact icon={DeleteSweepOutlined} tint="amber" label="Rebut" value={nb(rebut)} sub={total + rebut ? `${((100 * rebut) / (total + rebut)).toFixed(1).replace('.', ',')} % de la fabrication` : '—'} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <Panel title="Production par article" flush>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Article</TableCell><TableCell align="right">Produit</TableCell><TableCell align="right">Rebut</TableCell><TableCell align="right">Taux</TableCell></TableRow></TableHead>
                <TableBody>
                  {d?.synthese.map((x) => (
                    <TableRow key={x.article_id}>
                      <TableCell>{x.designation}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{nb(x.quantite)} <span style={{ color: tokens.steel, fontSize: 12 }}>{x.unite}</span></TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, color: x.rebut ? '#8A5A0B' : tokens.steel }}>{nb(x.rebut)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, color: tokens.steel }}>{x.quantite + x.rebut ? `${((100 * x.rebut) / (x.quantite + x.rebut)).toFixed(1).replace('.', ',')} %` : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {d && !d.synthese.length && <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune production sur la période.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Panel title="Rapports" flush>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>N°</TableCell><TableCell>Auteur</TableCell><TableCell>Production</TableCell><TableCell align="right">Rebut</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {d?.rapports.map((r) => (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/briques/rapports/${r.id}`)}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{dateFr(r.report_date)}</TableCell>
                      <TableCell sx={{ fontFamily: tokens.mono, fontSize: 13 }}>{r.numero}</TableCell>
                      <TableCell>{[r.employee_firstname, r.employee_name].filter(Boolean).join(' ') || r.saisi_par}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{r.lignes.filter((l) => l.quantite > 0).map((l) => `${l.code} ${nb(l.quantite)}`).join(' · ') || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, color: r.total_rebut ? '#8A5A0B' : tokens.steel }}>{nb(r.total_rebut)}</TableCell>
                      <TableCell>{r.observations && <Tooltip title={r.observations}><CommentOutlined sx={{ fontSize: 17, color: tokens.steel }} /></Tooltip>}</TableCell>
                    </TableRow>
                  ))}
                  {d && !d.rapports.length && <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun rapport sur la période.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default RapportsBriques;
